import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mutate } from 'swr';
import {
  APPROVAL_V2_URL,
  getApprovalSummarizeRequestUrl,
} from '@/constant/approval';
import { useDebugLogger } from '@/hooks/useDebugLogger';
import { useSSE } from '@/hooks/sse/useSSE';
import { fetchApi } from '@/utils/api/customAxios';
import { getNestedDataFetcher } from '@/utils/apis';
import type { ApprovalAiSummaryRequestResponse } from '@/types/approval';
import type {
  ApprovalViewAi,
  DocumentStatus,
} from '@/components/Approval/ApprovalView/types';

/**
 * UI 분기용 상태.
 *  - `idle`       — 응답에 ai 가 전혀 없거나 알 수 없는 상태. retry 버튼 노출.
 *  - `loading`    — 백엔드가 요약을 만들고 있음. 스피너 표시 + SSE 대기.
 *  - `success`    — 요약 텍스트 노출.
 *  - `no-content` — 본문이 비어 요약 불가. "본문 비어있음" 안내 + retry.
 *  - `error`      — 요약 도중 에러 발생. "요약 중 에러" 안내 + retry.
 */
export type ApprovalAiSummaryUiStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'no-content'
  | 'error';

export type UseApprovalAiSummaryResult = {
  status: ApprovalAiSummaryUiStatus;
  summary: string | null;
  canRetry: boolean; // retry / requestSummary 가능 여부. loading 이 아닐 때 항상 true
  retry: () => void; // "다시 요약" 버튼 핸들러 — POST 후 SSE complete 까지 대기
};

type UseApprovalAiSummaryArgs = {
  documentId: string | null | undefined;
  ai?: ApprovalViewAi | null; // v2 단건조회 응답의 ai 객체 (status/pipelineId/summary).
  content?: string | null; // v2 응답의 본문 (`ApprovalViewData.content`)
  subContent?: unknown[] | unknown | null; // v2 응답의 서브 컨텐츠. 1번 유형은 null
  documentStatus?: DocumentStatus | null; // v2 응답의 문서 상태 ('S'=임시저장 외 등). 임시저장이면 본문 빈 체크 제외, mount 시 자동 retry 1회
};

const SUCCESS = 'success';
const SKIPPED_NO_CONTENT = 'skipped_no_content';
const RUNNING = 'running';
const PENDING_STATUSES = new Set(['pending', 'running']);
/**
 * 요약이 다시 만들어질 수 있는 문서 상태 — 임시저장·진행·보류·반려(편집·재상신 가능).
 * 이 상태는 요약이 있어도 SSE 를 유지해 새 요약이 오면 바꿔 보여줌. 완료·취소 문서는 구독 안 함(브로커 연결 절약)
 */
const RESUMMARIZABLE_STATUSES = new Set<DocumentStatus>(['S', 'P', 'H', 'R']);

/** subContent 가 비어있는지 — 배열은 length 체크, 그 외는 truthy 체크. */
const isSubContentEmpty = (subContent: unknown): boolean => {
  if (subContent === null || subContent === undefined) return true;
  if (Array.isArray(subContent)) return subContent.length === 0;
  return false;
};

/** 품의 AI 요약 상태 + 액션 hook */
const useApprovalAiSummary = ({
  documentId,
  ai,
  content,
  subContent,
  documentStatus,
}: UseApprovalAiSummaryArgs): UseApprovalAiSummaryResult => {
  const logger = useDebugLogger();
  const loggerRef = useRef(logger);
  loggerRef.current = logger;

  const [status, setStatus] = useState<ApprovalAiSummaryUiStatus>('idle');
  const [summary, setSummary] = useState<string | null>(null);
  const [sseEnabled, setSseEnabled] = useState(false);

  // documentId 가 바뀌면 초기 상태 산정이 다시 일어나도록 ref 로 직전 docid 추적.
  const lastDocIdRef = useRef<string | null>(null);
  const autoRetryDocIdRef = useRef<string | null>(null); // 임시저장 자동 retry — documentId 변경 시 1회만 실행
  const resummarizeDocIdRef = useRef<string | null>(null); // 옛날 미완료 문서(pending/running + aiPipelineId 없음) — docId 당 새 요약 POST 1회만.

  // 구독 유지 여부 — 콜백(SSE 핸들러·재조회)이 최신 문서 상태를 보도록 ref
  const keepListeningRef = useRef(false);
  keepListeningRef.current = Boolean(
    documentStatus && RESUMMARIZABLE_STATUSES.has(documentStatus),
  );
  const closeSse = useCallback(() => {
    if (!keepListeningRef.current) setSseEnabled(false);
  }, []);

  // 최신 documentId 참조를 콜백에서 사용 (refetchSummary/retry 가 documentId 변경에 따라 재생성되지 않도록)
  const documentIdRef = useRef<string | null | undefined>(documentId);
  useEffect(() => {
    documentIdRef.current = documentId;
  }, [documentId]);

  // ----- GET 으로 최종 결과 재조회 -----

  /**
   * GET /api/v2/approval/{documentId} 의 ai — v2 요약 요청 결과는 v1 /summary 에 반영되지 않음
   * @param closeSseOnFinish — 결과가 확정되면 SSE 도 종료할지. complete 이벤트 핸들러에서는 true.
   * @returns `'success' | 'no-content' | 'unknown' | 'error'` — retry 분기 판단용.
   */
  const refetchSummary = useCallback(
    async (
      closeSseOnFinish = true,
    ): Promise<'success' | 'no-content' | 'unknown' | 'error'> => {
      const docId = documentIdRef.current;
      if (!docId) return 'unknown';
      try {
        const url = `${APPROVAL_V2_URL}/${docId}`;
        const data = await getNestedDataFetcher<{ ai?: ApprovalViewAi | null }>(
          url,
        );
        const ai = data?.ai;
        // 조회 화면 단건 캐시(문자열·['view'] 튜플 키)도 갱신 — 남은 화면이 옛 ai 를 다시 넣지 않게
        mutate(
          (key) => key === url || (Array.isArray(key) && key[0] === url),
        ).catch(() => undefined);
        if (ai?.status === SUCCESS && ai.summary) {
          setStatus('success');
          setSummary(ai.summary);
          if (closeSseOnFinish) closeSse();
          return 'success';
        }
        if (ai?.status === SKIPPED_NO_CONTENT) {
          setStatus('no-content');
          setSummary(null);
          if (closeSseOnFinish) closeSse();
          return 'no-content';
        }
        // 진행중이거나 결과 미확정 — 호출자가 다음 행동 결정.
        return 'unknown';
      } catch (e) {
        loggerRef.current.error('[AiSummary] v2 단건 ai 조회 실패:', e);
        setStatus('error');
        setSummary(null);
        if (closeSseOnFinish) closeSse();
        return 'error';
      }
    },
    [closeSse],
  );

  // ----- 새 요약 요청 (POST) -----
  const requestNewSummary = useCallback(async () => {
    const docId = documentIdRef.current;
    if (!docId) return;
    // SSE 먼저 enable (POST 응답 직후 들어오는 start 이벤트 손실 방지) 후 POST.
    setStatus('loading');
    setSummary(null);
    setSseEnabled(true);

    try {
      const res = await fetchApi<{ data: ApprovalAiSummaryRequestResponse }>({
        method: 'POST',
        url: getApprovalSummarizeRequestUrl(docId),
      });
      const pipelineId = res?.data?.aiPipelineId ?? null;

      if (!pipelineId) {
        // 본문 비어있음 — SSE 종료, 안내 노출.
        setStatus('no-content');
        setSseEnabled(false);
        return;
      }
      // pipelineId 존재 → SSE 대기 (complete / error 핸들러가 후속 처리)
    } catch (e) {
      loggerRef.current.error('[AiSummary] POST /summarize 실패:', e);
      setStatus('error');
      setSseEnabled(false);
    }
  }, []);

  // ----- 1. 초기 상태 계산 (L1 포함) -----

  useEffect(() => {
    if (!documentId) {
      setStatus('idle');
      setSummary(null);
      setSseEnabled(false);
      lastDocIdRef.current = null;
      autoRetryDocIdRef.current = null;
      resummarizeDocIdRef.current = null;
      return;
    }

    // documentId 가 바뀌면 stale 상태를 reset 후 새 응답 기준으로 다시 계산.
    if (lastDocIdRef.current !== documentId) {
      lastDocIdRef.current = documentId;
      setSummary(null);
      setSseEnabled(false);
      resummarizeDocIdRef.current = null;
    }

    const isEditableDoc = documentStatus === 'S';
    const keepListening = Boolean(
      documentStatus && RESUMMARIZABLE_STATUSES.has(documentStatus),
    );

    // 본문 빈 체크 — running 도 아니고 임시저장도 아니면 즉시 no-content.
    const contentText = typeof content === 'string' ? content.trim() : '';
    const hasSubContent = !isSubContentEmpty(subContent);
    const aiStatus = ai?.status ?? null;
    const isRunning = aiStatus === RUNNING || aiStatus === 'pending';

    if (!isEditableDoc && !contentText && !hasSubContent && !isRunning) {
      setStatus('no-content');
      setSummary(null);
      setSseEnabled(keepListening);
      return;
    }

    if (aiStatus === SUCCESS && ai?.summary) {
      setStatus('success');
      setSummary(ai.summary);
      setSseEnabled(keepListening);
      return;
    }

    if (aiStatus === SKIPPED_NO_CONTENT) {
      setStatus('no-content');
      setSummary(null);
      setSseEnabled(keepListening);
      return;
    }

    if (aiStatus && PENDING_STATUSES.has(aiStatus)) {
      // 진행 중 — loading + SSE 구독.
      setStatus('loading');
      setSummary(null);
      setSseEnabled(true);

      if (!ai?.pipelineId && !isEditableDoc) {
        // pending/running 인데 pipelineId 없음 = 요약이 완성된 적 없는 옛 문서 → 새 요약 POST.
        // docId 당 1회만. ('S' 임시저장은 아래 자동 retry effect 가 담당 → 중복 방지)
        if (resummarizeDocIdRef.current !== documentId) {
          resummarizeDocIdRef.current = documentId;
          void requestNewSummary();
        }
      }
      return;
    }

    // ai 도착 전(undefined)엔 상태 확정 보류 — '요약 중' 으로 대기해 깜빡임 방지.
    if (ai === undefined) {
      setStatus('loading');
      setSummary(null);
      setSseEnabled(false);
      return;
    }

    // 그 외 → idle (retry 노출). SSE 는 start 이벤트 손실 방지 위해 미리 켬.
    setStatus('idle');
    setSummary(null);
    setSseEnabled(true);
  }, [documentId, ai, content, subContent, documentStatus, requestNewSummary]);

  // ----- 2. SSE 구독 — 포탈 공용 useSSE 재사용 (talk-flow 와 동일 broker 설정) -----
  //
  // 연결 실패는 useSSE 가 조용히 종료 → loading 유지. 'approval.summarize.error' 만 error 전환.
  const sseTopics = useMemo(
    () => (sseEnabled && documentId ? [`approval:${documentId}`] : []),
    [sseEnabled, documentId],
  );

  const sseHandlers = useMemo(
    () => ({
      // 새 요약 시작(저장·상신 후 서버 재생성 포함) — 기존 요약을 내리고 스피너
      'approval.summarize.start': () => {
        setStatus('loading');
        setSummary(null);
      },
      // 진행 로그성 이벤트 — 상태는 그대로 loading 유지.
      'approval.summarize.summary.start': () => {
        // noop
      },
      'approval.summarize.summary.complete': () => {
        // noop
      },
      // 완료 — GET 으로 최종 결과 재조회.
      'approval.summarize.complete': async () => {
        loggerRef.current.log('[AiSummary] complete — refetch v2 단건 ai');
        await refetchSummary(true);
      },
      // 요약 자체 실패 — 이때만 error.
      'approval.summarize.error': () => {
        loggerRef.current.error('[AiSummary] summarize error event');
        setStatus('error');
        setSummary(null);
        closeSse();
      },
    }),
    [closeSse, refetchSummary],
  );

  useSSE(sseTopics, sseHandlers, sseEnabled);

  // ----- 3. retry (POST) -----

  // 가장 최신 status 를 retry 클로저에서 참조하기 위한 ref.
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const retry = useCallback(async () => {
    const docId = documentIdRef.current;
    if (!docId) return;

    // error 상태에서 retry → GET 으로 먼저 결과 확인. success/skipped 이면 그걸로 끝.
    if (statusRef.current === 'error') {
      // GET 호출 중에도 표시는 loading 으로.
      setStatus('loading');
      setSummary(null);
      const result = await refetchSummary(false);
      if (result === 'success' || result === 'no-content') {
        // GET 으로 끝남.
        return;
      }
      // 진행중/에러/미확정 → 새 POST.
      await requestNewSummary();
      return;
    }

    // 그 외 상태(idle / no-content) → 바로 POST.
    await requestNewSummary();
  }, [refetchSummary, requestNewSummary]);

  // ----- 4. 임시저장(status === 'S') 자동 retry — documentId 당 1회 -----
  //
  // 이미 success 요약이 있으면 skip, ai undefined(응답 도착 전)면 결정 보류.
  useEffect(() => {
    if (!documentId) return;
    if (documentStatus !== 'S') return;
    if (autoRetryDocIdRef.current === documentId) return;
    // 응답 아직 안 도착 → 결정 보류 (다음 렌더에 도착 후 결정)
    if (ai === undefined) return;
    // 이미 success 요약이 있으면 자동 retry skip + 마킹.
    if (ai?.status === SUCCESS && Boolean(ai?.summary)) {
      autoRetryDocIdRef.current = documentId;
      return;
    }
    autoRetryDocIdRef.current = documentId;
    void requestNewSummary();
  }, [documentId, documentStatus, ai, requestNewSummary]);

  return {
    status,
    summary,
    canRetry: status !== 'loading',
    retry,
  };
};

export default useApprovalAiSummary;
